import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Paper,
  Divider,
  Avatar,
  Fade,
  Stack,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';

import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';


const ACCENT = '#0655A5';
const NEUTRAL = '#64748b';
const BG_SOFT = '#f8fafc';

// Estilo de inputs Premium y Compactos (V4)
const premiumInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#ffffff',
    fontSize: '0.85rem',
    transition: 'all 0.2s',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.15)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8', fontWeight: 500, fontSize: '0.8rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

const ModernDropPaper = (props) => (
  <Paper
    {...props}
    elevation={12}
    sx={{
      borderRadius: 2.5,
      mt: 0.5,
      border: '1px solid #f1f5f9',
      boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
      '& .MuiAutocomplete-listbox': {
        p: 0.5,
        maxHeight: '35vh',
        '& .MuiAutocomplete-option': {
          borderRadius: 1,
          mb: 0.25,
          fontSize: '0.85rem',
          '&:hover': { bgcolor: '#f1f5f9' },
          '&[aria-selected="true"]': { bgcolor: 'rgba(6,85,165,0.05)', color: ACCENT, fontWeight: 600 },
        },
      },
    }}
  />
);

const GroupHeader = ({ title, icon, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar sx={{ bgcolor: 'rgba(6,85,165,0.06)', color: ACCENT, width: 28, height: 28 }}>
        {React.cloneElement(icon, { fontSize: 'small' })}
      </Avatar>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.1px' }}>
        {title}
      </Typography>
    </Box>
    {action}
  </Box>
);

const ModalNivelAut = ({
  isOpen,
  handleClose,
  formData,
  handleInputChange,
  handleSave,
  dataEmpresa = [],
  dataSucursal = [],
  dataCentrocosto = [],
  dataDepartamento = [],
  dataUsuario = [],
  dataUsuarioAutorizadores = [],
  dataUsuarioTesoreros = [],
  dataDivisas = []
}) => {

  const handleAddDynamicItem = (fieldName, maxItems) => {
    const list = formData?.[fieldName] || [];
    if (!maxItems || list.length < maxItems) handleInputChange(fieldName, [...list, '']);
  };

  const handleRemoveDynamicItem = (fieldName, index) => {
    const newList = [...(formData?.[fieldName] || [])];
    newList.splice(index, 1);
    handleInputChange(fieldName, newList);
  };

  const handleUpdateDynamicItem = (fieldName, index, val) => {
    const newList = [...(formData?.[fieldName] || [])];
    newList[index] = val;
    handleInputChange(fieldName, newList);
  };

  const handleMoveDynamicItem = (fieldName, index, direction) => {
    const newList = [...(formData?.[fieldName] || [])];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newList.length) return;
    const temp = newList[index];
    newList[index] = newList[targetIndex];
    newList[targetIndex] = temp;
    handleInputChange(fieldName, newList);
  };

  // Validación básica: Empresa requerida y al menos 1 autorizador
  const autorizadoresList = (formData?.Autorizadores || []).filter(Boolean);
  const isFormValid = Boolean(formData?.Empresa) && autorizadoresList.length > 0;

  const renderItem = (props, option, idKey, labelKey) => {
    const isNew = option[idKey] === 'nuevo';
    return (
      <ListItem {...props} key={option[idKey]} sx={{ px: 1, py: 0.5 }}>
        <ListItemIcon sx={{ minWidth: 28 }}>
          {isNew ? <AddIcon sx={{ color: '#6366f1', fontSize: '1rem' }} /> : <PersonIcon sx={{ color: '#94a3b8', fontSize: '0.9rem' }} />}
        </ListItemIcon>
        <ListItemText
          primary={option[labelKey]}
          primaryTypographyProps={{ fontWeight: isNew ? 700 : 500, color: isNew ? '#6366f1' : '#334155', fontSize: '0.85rem' }}
        />
      </ListItem>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      disableRestoreFocus
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)',
        },
      }}
    >
      {/* Header Más Compacto */}
      <Box sx={{ px: 3, py: 1.5, background: `linear-gradient(135deg, ${ACCENT} 0%, #1e40af 100%)`, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', p: 0.5 }}>
          <SettingsSuggestIcon sx={{ fontSize: '1.1rem' }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'white', lineHeight: 1, fontSize: '1rem' }}>
            {formData?.ID ? 'Ajustes del Nivel' : 'Nuevo Nivel de Autorización'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem' }}>
            Gestión de jerarquías y flujos de aprobación.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white', opacity: 0.85, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: '#ffffff', minHeight: 'fit-content' }}>
        {/* Uso de CSS Grid nativo para asegurar división 50/50 perfecta sin errores de breakpoints */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 3,
          width: '100%',
          height: '100%'
        }}>

          {/* PANEL IZQUIERDO: Configuración */}
          <Box sx={{
            p: 2.5,
            borderRadius: 3.5,
            border: '1px solid #f1f5f9',
            bgcolor: '#fcfdfe',
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}>
            <GroupHeader title="Configuración" icon={<BusinessIcon />} />
            <Stack spacing={2}>
              <Autocomplete
                fullWidth
                options={dataEmpresa || []}
                getOptionLabel={(o) => o.NOMBRE_EMPRESA || ''}
                value={dataEmpresa.find(e => e.EMPRESAID === formData?.Empresa) || null}
                onChange={(_, v) => handleInputChange('Empresa', v?.EMPRESAID)}
                PaperComponent={ModernDropPaper}
                renderInput={(p) => <TextField {...p} label="Empresa Responsable" sx={premiumInputSx} size="small" />}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Autocomplete
                  options={dataSucursal || []}
                  getOptionLabel={(o) => o.NOMBRE_SUCURSAL || ''}
                  value={dataSucursal.find(s => s.NUMERO_SUCURSAL === formData?.Sucursal) || null}
                  onChange={(_, v) => handleInputChange('Sucursal', v?.NUMERO_SUCURSAL)}
                  PaperComponent={ModernDropPaper}
                  renderInput={(p) => <TextField {...p} label="Sucursal" sx={premiumInputSx} size="small" />}
                />
                <Autocomplete
                  options={dataDivisas || []}
                  getOptionLabel={(o) => o.NOMBRE_DIVISA || ''}
                  value={dataDivisas.find(d => d.ID_DIVISA === formData?.Divisa) || null}
                  onChange={(_, v) => handleInputChange('Divisa', v?.ID_DIVISA)}
                  PaperComponent={ModernDropPaper}
                  renderInput={(p) => <TextField {...p} label="Divisa" sx={premiumInputSx} size="small" />}
                />
              </Box>

              <Autocomplete
                options={[...(formData?.Empresa ? [{ CCID: 'nuevo', CENTRO_COSTO: '➕ Nuevo centro de costo' }] : []), ...(dataCentrocosto || [])]}
                getOptionLabel={(o) => o.CENTRO_COSTO ? (o.NUMERO_CC ? `${o.CENTRO_COSTO} - ${o.NUMERO_CC}` : o.CENTRO_COSTO) : ''}
                value={dataCentrocosto.find(c => c.CCID === formData?.CentroCosto) || null}
                onChange={(_, v) => handleInputChange('CentroCosto', v?.CCID)}
                PaperComponent={ModernDropPaper}
                renderInput={(p) => <TextField {...p} label="Centro de Costo" sx={premiumInputSx} size="small" />}
              />

              <Autocomplete
                options={[...(formData?.Empresa ? [{ DEPARTAMENTOID: 'nuevo', NOMBRE_DEPARTAMENTO: '➕ Nuevo departamento' }] : []), ...(dataDepartamento || [])]}
                getOptionLabel={(o) => o.NOMBRE_DEPARTAMENTO || ''}
                value={dataDepartamento.find(d => d.DEPARTAMENTOID === formData?.Departamento) || null}
                onChange={(_, v) => handleInputChange('Departamento', v?.DEPARTAMENTOID)}
                PaperComponent={ModernDropPaper}
                renderInput={(p) => <TextField {...p} label="Departamento" sx={premiumInputSx} size="small" />}
              />

              <Autocomplete
                options={[...(formData?.Empresa ? [{ USUARIOID: 'nuevo', USER_NAME: '➕ Nuevo usuario' }] : []), ...(dataUsuario || [])]}
                getOptionLabel={(o) => o.USER_NAME || ''}
                value={dataUsuario.find(u => u.USUARIOID === formData?.Comprador) || null}
                onChange={(_, v) => handleInputChange('Comprador', v?.USUARIOID)}
                PaperComponent={ModernDropPaper}
                renderOption={(pr, o) => renderItem(pr, o, 'USUARIOID', 'USER_NAME')}
                renderInput={(p) => <TextField {...p} label="Gestor de Compras" sx={premiumInputSx} size="small" />}
              />
            </Stack>
          </Box>

          {/* PANEL DERECHO: Autorización y Cadena de Mando */}
          <Box sx={{
            p: 2.5,
            borderRadius: 3.5,
            border: '1px solid #f1f5f9',
            bgcolor: '#fcfdfe',
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}>
            <GroupHeader
              title="Cadena de Mando"
              icon={<SecurityIcon />}
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: '0.85rem !important' }} />}
                  onClick={() => handleAddDynamicItem('Autorizadores')}
                  sx={{
                    color: ACCENT,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    bgcolor: 'rgba(6,85,165,0.06)',
                    px: 1.2,
                    py: 0.3,
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(6,85,165,0.12)' }
                  }}
                >
                  Nivel
                </Button>
              }
            />

            <Stack spacing={1.25} sx={{ mb: 2, flexGrow: 1 }}>
              {(formData?.Autorizadores || []).map((value, idx) => (
                <Box key={`aut-${idx}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  <Box
                    sx={{
                      minWidth: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: 'rgba(6,85,165,0.08)',
                      color: ACCENT,
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Autocomplete
                    fullWidth
                    options={[...(formData?.Empresa ? [{ USUARIOID: 'nuevo', USER_NAME: '➕ Nuevo usuario' }] : []), ...(dataUsuarioAutorizadores || [])]}
                    getOptionLabel={(o) => o.USER_NAME || ''}
                    value={dataUsuarioAutorizadores.find(u => u.USUARIOID === value) || null}
                    onChange={(_, v) => handleUpdateDynamicItem('Autorizadores', idx, v?.USUARIOID)}
                    PaperComponent={ModernDropPaper}
                    renderOption={(pr, o) => renderItem(pr, o, 'USUARIOID', 'USER_NAME')}
                    renderInput={(p) => <TextField {...p} label={`Autorizador Nivel ${idx + 1}`} sx={premiumInputSx} size="small" />}
                  />
                  {/* Botones de reordenamiento Subir/Bajar */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                    <IconButton
                      size="small"
                      disabled={idx === 0}
                      onClick={() => handleMoveDynamicItem('Autorizadores', idx, -1)}
                      sx={{ p: 0.2, color: '#94a3b8', '&:hover': { color: ACCENT }, '&.Mui-disabled': { opacity: 0.3 } }}
                      title="Subir nivel"
                    >
                      <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={idx === (formData?.Autorizadores || []).length - 1}
                      onClick={() => handleMoveDynamicItem('Autorizadores', idx, 1)}
                      sx={{ p: 0.2, color: '#94a3b8', '&:hover': { color: '#0655A5' }, '&.Mui-disabled': { opacity: 0.3 } }}
                      title="Bajar nivel"
                    >
                      <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />
                    </IconButton>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveDynamicItem('Autorizadores', idx)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                    <RemoveCircleOutlineIcon sx={{ fontSize: '1.05rem' }} />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            <Divider sx={{ mb: 2, borderStyle: 'dashed', opacity: 0.6 }} />

            <GroupHeader title="Tesorero" icon={<PaymentIcon />} />
            <Stack spacing={1.5}>
              {(formData?.Tesoreros || []).map((value, idx) => (
                <Box key={`tes-${idx}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Autocomplete
                    fullWidth
                    options={[...(formData?.Empresa ? [{ USUARIOID: 'nuevo', USER_NAME: '➕ Nuevo usuario' }] : []), ...(dataUsuarioTesoreros || [])]}
                    getOptionLabel={(o) => o.USER_NAME || ''}
                    value={dataUsuarioTesoreros.find(u => u.USUARIOID === value) || null}
                    onChange={(_, v) => handleUpdateDynamicItem('Tesoreros', idx, v?.USUARIOID)}
                    PaperComponent={ModernDropPaper}
                    renderOption={(pr, o) => renderItem(pr, o, 'USUARIOID', 'USER_NAME')}
                    renderInput={(p) => <TextField {...p} label="Tesorero Responsable" sx={premiumInputSx} size="small" />}
                  />
                  <IconButton size="small" onClick={() => handleRemoveDynamicItem('Tesoreros', idx)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                    <RemoveCircleOutlineIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Box>
              ))}
              {(formData?.Tesoreros || []).length === 0 && (
                <Button
                  fullWidth
                  size="small"
                  onClick={() => handleAddDynamicItem('Tesoreros', 1)}
                  sx={{ py: 1, border: '1px dashed #e2e8f0', borderRadius: 2, color: NEUTRAL, textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Vincular Tesorero
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: BG_SOFT, gap: 2, borderTop: '1px solid #f1f5f9' }}>
        <Button onClick={handleClose} sx={{ px: 3, fontWeight: 700, color: NEUTRAL, fontSize: '0.8rem' }}>
          DESCARTAR
        </Button>
        <Button
          variant="contained"
          disabled={!isFormValid}
          onClick={handleSave}
          sx={{
            px: 4,
            py: 0.8,
            fontWeight: 800,
            borderRadius: 2,
            bgcolor: ACCENT,
            textTransform: 'none',
            fontSize: '0.85rem',
            boxShadow: '0 4px 14px rgba(6,85,165,0.25)',
            '&:hover': { bgcolor: '#05478a', transform: 'translateY(-1px)' },
            '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#94a3b8' }
          }}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNivelAut;